/**
 * IntakeAssignmentFilters Component
 *
 * Filters for intake assignment workflow
 */

import {
  Box,
  Typography,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  SelectChangeEvent,
} from '@mui/material';

interface IntakeAssignmentFiltersProps {
  targetUtilization: number;
  onTargetUtilizationChange: (value: number) => void;
  selectedTerritories: string[];
  availableTerritories: string[];
  onTerritoriesChange: (territories: string[]) => void;
  selectedPriorities: ('HIGH' | 'MEDIUM' | 'LOW')[];
  onPrioritiesChange: (priorities: ('HIGH' | 'MEDIUM' | 'LOW')[]) => void;
  selectedProviderType: 'RN' | 'PT' | 'OT';
  onProviderTypeChange: (providerType: 'RN' | 'PT' | 'OT') => void;
}

export default function IntakeAssignmentFilters({
  targetUtilization,
  onTargetUtilizationChange,
  selectedTerritories,
  availableTerritories,
  onTerritoriesChange,
  selectedPriorities,
  onPrioritiesChange,
  selectedProviderType,
  onProviderTypeChange,
}: IntakeAssignmentFiltersProps) {
  const handleUtilizationChange = (_event: Event, value: number | number[]) => {
    onTargetUtilizationChange(value as number);
  };

  const handleTerritoriesChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onTerritoriesChange(typeof value === 'string' ? value.split(',') : value);
  };

  const handlePrioritiesChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onPrioritiesChange((typeof value === 'string' ? value.split(',') : value) as ('HIGH' | 'MEDIUM' | 'LOW')[]);
  };

  const handleProviderTypeChange = (event: SelectChangeEvent<string>) => {
    onProviderTypeChange(event.target.value as 'RN' | 'PT' | 'OT');
  };

  return (
    <Box
      sx={{
        mb: 3,
        p: 2.5,
        bgcolor: '#fafafa',
        borderRadius: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          flexWrap: 'wrap',
        }}
      >
        {/* Target Utilization Slider */}
        <Box sx={{ minWidth: 300, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.5 }}>
            <Typography variant="body2" fontWeight={600} color="text.primary">
              Target Utilization:
            </Typography>
            <Typography variant="h5" fontWeight={700} color="primary.main">
              {targetUtilization}%
            </Typography>
          </Box>
          <Slider
            value={targetUtilization}
            onChange={handleUtilizationChange}
            min={50}
            max={100}
            step={5}
            marks={[
              { value: 50, label: '50%' },
              { value: 75, label: '75%' },
              { value: 100, label: '100%' },
            ]}
            valueLabelDisplay="auto"
            sx={{
              '& .MuiSlider-thumb': {
                width: 22,
                height: 22,
                boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
              },
              '& .MuiSlider-track': {
                height: 8,
              },
              '& .MuiSlider-rail': {
                height: 8,
                opacity: 0.3,
              },
            }}
          />
        </Box>

        {/* Territory Filter */}
        <Box sx={{ minWidth: 200 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.5}>
            TERRITORY
          </Typography>
          <Select
            multiple
            value={selectedTerritories}
            onChange={handleTerritoriesChange}
            displayEmpty
            variant="outlined"
            size="small"
            sx={{
              minWidth: 200,
              bgcolor: 'white',
              borderRadius: 1,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'divider',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '& .MuiSelect-select': {
                py: 1,
              },
            }}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <Typography color="text.secondary">All Territories</Typography>;
              }
              if (selected.length === availableTerritories.length) {
                return <Typography fontWeight={500}>All Territories</Typography>;
              }
              return <Typography fontWeight={500}>{selected.join(', ')}</Typography>;
            }}
          >
            <MenuItem
              value="all"
              onClick={() => {
                if (selectedTerritories.length === availableTerritories.length) {
                  onTerritoriesChange([]);
                } else {
                  onTerritoriesChange(availableTerritories);
                }
              }}
            >
              <strong>All Territories</strong>
            </MenuItem>
            {availableTerritories.map((territory) => (
              <MenuItem key={territory} value={territory}>
                {territory}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Priority Filter */}
        <Box sx={{ minWidth: 200 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.5}>
            PRIORITY
          </Typography>
          <Select
            multiple
            value={selectedPriorities}
            onChange={handlePrioritiesChange}
            displayEmpty
            variant="outlined"
            size="small"
            sx={{
              minWidth: 200,
              bgcolor: 'white',
              borderRadius: 1,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'divider',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '& .MuiSelect-select': {
                py: 1,
              },
            }}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <Typography color="text.secondary">All Priorities</Typography>;
              }
              if (selected.length === 3) {
                return <Typography fontWeight={500}>All Priorities</Typography>;
              }
              return <Typography fontWeight={500}>{selected.join(', ')}</Typography>;
            }}
          >
            <MenuItem
              value="all"
              onClick={() => {
                if (selectedPriorities.length === 3) {
                  onPrioritiesChange([]);
                } else {
                  onPrioritiesChange(['HIGH', 'MEDIUM', 'LOW']);
                }
              }}
            >
              <strong>All Priorities</strong>
            </MenuItem>
            <MenuItem value="HIGH">HIGH</MenuItem>
            <MenuItem value="MEDIUM">MEDIUM</MenuItem>
            <MenuItem value="LOW">LOW</MenuItem>
          </Select>
        </Box>

        {/* Provider Type Filter */}
        <Box sx={{ minWidth: 150 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.5}>
            PROVIDER TYPE
          </Typography>
          <Select
            value={selectedProviderType}
            onChange={handleProviderTypeChange}
            variant="outlined"
            size="small"
            sx={{
              minWidth: 150,
              bgcolor: 'white',
              borderRadius: 1,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'divider',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '& .MuiSelect-select': {
                py: 1,
              },
            }}
          >
            <MenuItem value="RN">RN</MenuItem>
            <MenuItem value="PT">PT</MenuItem>
            <MenuItem value="OT">OT</MenuItem>
          </Select>
        </Box>
      </Box>
    </Box>
  );
}
